export const CALCULATOR_LOOT_SORT_BY_VALUES = Object.freeze([
    "price",
    "grade",
    "droprate",
    "expected_value",
    "exp",
    "totem_exp",
    "alphabetical",
    "item_id",
    "database",
    "evidence",
]);

export const CALCULATOR_LOOT_SORT_ORDER_VALUES = Object.freeze(["desc", "asc"]);

const SORT_BY = new Set(CALCULATOR_LOOT_SORT_BY_VALUES);
const SORT_ORDER = new Set(CALCULATOR_LOOT_SORT_ORDER_VALUES);
const SORT_VALUE_FIELDS = Object.freeze([
    "price",
    "grade",
    "dropRate",
    "expectedValue",
    "fishingExperience",
    "totemExperience",
    "evidence",
    "database",
    "itemId",
    "label",
]);
const NON_KNOWN_NUMERIC_STATE_RANK = Object.freeze({
    unavailable: 0,
    unknown: 1,
});

export const DEFAULT_CALCULATOR_LOOT_SORT = Object.freeze({
    by: "price",
    order: "desc",
});

function requireRecord(value, name) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError(`${name} must be an object`);
    }
    return value;
}

export function parseCalculatorLootSort(value) {
    const sort = requireRecord(value, "calculator loot sort");
    const fields = Object.keys(sort).sort();
    if (fields.length !== 2 || fields[0] !== "by" || fields[1] !== "order") {
        throw new TypeError("calculator loot sort must contain exactly by and order");
    }
    if (!SORT_BY.has(sort.by)) {
        throw new TypeError(`unsupported calculator loot sort field: ${String(sort.by)}`);
    }
    if (!SORT_ORDER.has(sort.order)) {
        throw new TypeError(`unsupported calculator loot sort order: ${String(sort.order)}`);
    }
    return Object.freeze({ by: sort.by, order: sort.order });
}

export function calculatorLootNumericSortValue(value) {
    if (value?.state === "known") {
        if (!Number.isFinite(value.value) || value.value < 0) {
            throw new TypeError("known calculator loot sort values must be finite and non-negative");
        }
        return Object.freeze({ state: "known", value: value.value });
    }
    if (value?.state === "unknown" || value?.state === "unavailable") {
        return Object.freeze({ state: value.state });
    }
    throw new TypeError("calculator loot sort values must be known, unknown, or unavailable");
}

export function calculatorLootEvidenceSortValue(segments) {
    if (!Array.isArray(segments)) {
        throw new TypeError("calculator loot evidence segments must be an array");
    }
    if (segments.length === 0) {
        return Object.freeze({ state: "unknown" });
    }
    let strongest = 0;
    for (const segment of segments) {
        const sortRank = segment?.sortRank;
        if (!Number.isFinite(sortRank) || sortRank < 0) {
            throw new TypeError(
                "calculator loot evidence segment sortRank must be finite and non-negative",
            );
        }
        strongest = Math.max(strongest, sortRank);
    }
    return Object.freeze({ state: "known", value: strongest });
}

/**
 * Creates the one strict sort-value shape shared by quantified Sankey rows and
 * Data-editor rows. Callers remain responsible for selecting the semantic row,
 * evidence segments, and database rank; this function only validates and
 * normalizes those already-resolved typed values.
 */
export function calculatorLootRowSortValues(value) {
    const record = requireRecord(value, "calculator loot row sort values");
    const fields = Object.keys(record).sort();
    const expected = [...SORT_VALUE_FIELDS].sort();
    if (
        fields.length !== expected.length
        || fields.some((field, index) => field !== expected[index])
    ) {
        throw new TypeError(
            `calculator loot row sort values must contain exactly ${expected.join(", ")}`,
        );
    }
    if (typeof record.label !== "string") {
        throw new TypeError("calculator loot row sort label must be a string");
    }
    return Object.freeze({
        price: calculatorLootNumericSortValue(record.price),
        grade: calculatorLootNumericSortValue(record.grade),
        dropRate: calculatorLootNumericSortValue(record.dropRate),
        expectedValue: calculatorLootNumericSortValue(record.expectedValue),
        fishingExperience: calculatorLootNumericSortValue(record.fishingExperience),
        totemExperience: calculatorLootNumericSortValue(record.totemExperience),
        evidence: calculatorLootNumericSortValue(record.evidence),
        database: calculatorLootNumericSortValue(record.database),
        itemId: calculatorLootNumericSortValue(record.itemId),
        label: record.label,
    });
}

function compareNumericState(left, right, order) {
    const leftKnown = left.state === "known";
    const rightKnown = right.state === "known";
    if (leftKnown !== rightKnown) {
        return leftKnown ? -1 : 1;
    }
    if (!leftKnown) {
        return NON_KNOWN_NUMERIC_STATE_RANK[left.state]
            - NON_KNOWN_NUMERIC_STATE_RANK[right.state];
    }
    const delta = left.value - right.value;
    return order === "asc" ? delta : -delta;
}

function rowSortValues(row) {
    return calculatorLootRowSortValues(row?.sortValues);
}

function compareSelectedField(left, right, sort) {
    switch (sort.by) {
        case "alphabetical": {
            const compared = left.label.localeCompare(right.label, undefined, {
                numeric: true,
                sensitivity: "base",
            });
            return sort.order === "asc" ? compared : -compared;
        }
        case "item_id":
            return compareNumericState(
                left.itemId,
                right.itemId,
                sort.order,
            );
        case "grade":
            return compareNumericState(
                left.grade,
                right.grade,
                sort.order,
            );
        case "droprate":
            return compareNumericState(
                left.dropRate,
                right.dropRate,
                sort.order,
            );
        case "expected_value":
            return compareNumericState(
                left.expectedValue,
                right.expectedValue,
                sort.order,
            );
        case "exp":
            return compareNumericState(
                left.fishingExperience,
                right.fishingExperience,
                sort.order,
            );
        case "totem_exp":
            return compareNumericState(
                left.totemExperience,
                right.totemExperience,
                sort.order,
            );
        case "evidence":
            return compareNumericState(
                left.evidence,
                right.evidence,
                sort.order,
            );
        case "price":
            return compareNumericState(
                left.price,
                right.price,
                sort.order,
            );
        case "database":
            // Database order is itself authoritative. As in the prior UI,
            // selecting Ascending does not reverse that source order.
            return compareNumericState(
                left.database,
                right.database,
                "asc",
            );
        default:
            throw new TypeError(`unsupported calculator loot sort field: ${sort.by}`);
    }
}

function compareValidatedSortValues(left, right, sort) {
    const selected = compareSelectedField(left, right, sort);
    if (selected !== 0) {
        return selected;
    }
    const database = compareNumericState(
        left.database,
        right.database,
        "asc",
    );
    if (database !== 0) {
        return database;
    }
    return compareNumericState(
        left.itemId,
        right.itemId,
        "asc",
    );
}

export function compareCalculatorLootRows(left, right, value = DEFAULT_CALCULATOR_LOOT_SORT) {
    const sort = parseCalculatorLootSort(value);
    return compareValidatedSortValues(
        rowSortValues(left),
        rowSortValues(right),
        sort,
    );
}

export function sortCalculatorLootRows(rows, value = DEFAULT_CALCULATOR_LOOT_SORT) {
    if (!Array.isArray(rows)) {
        throw new TypeError("calculator loot rows must be an array");
    }
    const sort = parseCalculatorLootSort(value);
    return rows
        .map((row) => ({ row, sortValues: rowSortValues(row) }))
        .sort((left, right) => compareValidatedSortValues(
            left.sortValues,
            right.sortValues,
            sort,
        ))
        .map(({ row }) => row);
}
